import React from 'react';

import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';

import messages from './messages';
import Icon from '@/ui/components/Icon';

const trueAnswers = [
  'YES',
  'RECOMMENDED',
  'YES_CLEANED_WITH_WATER',
  'YES_MAGIGOO_HT',
];

const maybeAnswers = [
  'YES_STICK_GLUE',
  'YES_TAPE',
  'YES_ENCLOSURE',
  'YES_WINDOW_CLEANER',
  'YES_WINDOW_CLEANER_WITH_GLUE',
];

const messageAnswers = [
  'RECOMMENDED',
  'YES_STICK_GLUE',
  'YES_TAPE',
  'YES_ENCLOSURE',
  'YES_WINDOW_CLEANER',
  'NOT_RECOMMENDED',
  'YES_CLEANED_WITH_WATER',
  'YES_MAGIGOO_HT',
  'NECESSARY',
];

export const Wrapper = styled.div<{
  value?: boolean | string;
  $maybe?: boolean;
  $left?: boolean;
  $isRecommended?: boolean;
}>`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: ${({ $left }) => ($left ? 'flex-start' : 'center')};
  ${({ $left }) => $left && ` padding-left: 10px; padding-right: 10px;`}
  flex-direction: column;
  color: ${({ value, $maybe, $isRecommended }) => {
    if ($isRecommended) return '#9C8408'; // color for "RECOMMENDED"
    if ($maybe) return '#ffb600';
    if (value === undefined) return 'inherit';
    return value === true ||
      (typeof value !== 'boolean' && trueAnswers.includes(value)) // value 1?
      ? '#5DAF0B'
      : '#CB2C2C';
  }};
  small {
    color: ${({ $isRecommended }) => ($isRecommended ? '#9C8408' : '#777')};
    font-size: 10px;
    line-height: 1.5;
  }
`;

export const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  .fa-check {
    font-size: 18px;
    height: 21px;
    width: 18px;
  }
  .fa-triangle-exclamation,
  .fa-circle-xmark {
    font-size: 16px;
    height: 16px;
    width: 16px;
  }
  .fa-circle-xmark {
    color: #e84040;
  }
`;

export function booleanAggregate(vals) {
  const first = vals[0] || 0;
  let allSame = true;

  vals.forEach(value => {
    if (value !== first) {
      allSame = false;
    }
  });

  return allSame ? first : undefined;
}

interface ValueProps {
  value: string;
  isRecommended: boolean;
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const Value = ({ value, isRecommended }: ValueProps) => {
  const translate = useTranslations();

  if (isRecommended) {
    return (
      <small>
        {capitalizeFirstLetter(
          translate('app-containers-MaterialsPage-recommended')
        )}
      </small>
    );
  }

  if (typeof value === 'string') {
    // Comma-separated values
    if (value.includes(',')) {
      const [, secondaryText, thirdText] = value.split(', ');

      return (
        <>
          {secondaryText && (
            <small>
              {messageAnswers.includes(secondaryText)
                ? translate(messages[secondaryText.toLowerCase()])
                : secondaryText}
            </small>
          )}
          {thirdText && <small>{thirdText}</small>}
        </>
      );
    }

    // Single message answers
    if (messageAnswers.includes(value)) {
      return <small>{translate(messages[value.toLowerCase()])}</small>;
    }
  }

  return null;
};

interface ContentProps {
  reversed: boolean;
  cellValue: boolean;
  value: string;
  peiExtra: boolean;
  isRecommended: boolean;
}

const Content = ({
  reversed,
  cellValue,
  value,
  peiExtra,
  isRecommended,
}: ContentProps) => {
  const translate = useTranslations();

  const icon = React.useMemo(() => {
    let icon: IconName = 'circle-xmark';

    if (cellValue) {
      icon = 'check';
    } else if (maybeAnswers.includes(value)) {
      icon = 'triangle-exclamation';
    }

    return icon;
  }, [cellValue, value]);

  // If recommended, show custom text instead of icon
  if (isRecommended) {
    return <Value value={value} isRecommended={true} />;
  }

  return (
    <>
      {reversed ? (
        translate(messages[cellValue ? 'yes' : 'no'])
      ) : cellValue === undefined ? (
        '-'
      ) : (
        <IconWrapper>
          <Icon name={icon} style="solid" />
        </IconWrapper>
      )}

      {peiExtra ? (
        <small>{translate(messages['necessary'])}</small>
      ) : (
        <Value value={value} isRecommended={false} />
      )}
    </>
  );
};

/**
 * @todo translation
 * @todo refactor
 * @todo documentation - reversed? maybeAnswers?
 */
export default function BooleanValue({
  value,
  reversed = false,
  peiExtra = false,
  $left = false,
}) {
  const isRecommended = value === 'RECOMMENDED';

  const cellValue = React.useMemo(() => {
    if (typeof value === 'string' && value.includes(',')) {
      const [mainText] = value.split(', ');
      return mainText === 'YES' || trueAnswers.includes(mainText);
    }
    return value === true || value === 1 || trueAnswers.includes(value);
  }, [value]);

  if (value === undefined) {
    return (
      <Wrapper $left={$left} value={value}>
        -
      </Wrapper>
    );
  }

  return (
    <Wrapper
      value={reversed ? !cellValue : cellValue}
      $maybe={maybeAnswers.includes(value)}
      $left={$left}
      $isRecommended={isRecommended}
    >
      <Content
        reversed={reversed}
        cellValue={cellValue}
        value={value}
        peiExtra={peiExtra}
        isRecommended={isRecommended}
      />
    </Wrapper>
  );
}
